import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, GitMerge, AlertTriangle, Truck, ExternalLink, CheckCircle2 } from "lucide-react";

// Admin tool to find + MANUALLY merge duplicate-VIN trucks (the same truck entered twice).
// One merge at a time: pick which record to KEEP (target), then merge each other into it.
// Mirrors the same-truck lot merge — no hard deletes, parts + cost recovery consolidated.
export function DonorDuplicatesDialog({ open, onOpenChange, showCost, onMerged, onOpenDonor }) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [keepBy, setKeepBy] = useState({});    // {vin: donorId to keep}
  const [merging, setMerging] = useState(null); // sourceId currently merging

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/donors/duplicates");
      setGroups(data.groups || []);
      const kb = {};
      (data.groups || []).forEach((g) => {
        const prim = g.donors.find((d) => d.is_primary) || [...g.donors].sort((a, b) => b.parts - a.parts)[0];
        kb[g.vin] = prim?.id;
      });
      setKeepBy(kb);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const merge = async (vin, source, target) => {
    const src = groups.find((g) => g.vin === vin)?.donors.find((d) => d.id === source);
    const tgt = groups.find((g) => g.vin === vin)?.donors.find((d) => d.id === target);
    if (!window.confirm(`Merge ${src?.stock_number} INTO ${tgt?.stock_number}?\n\nAll parts (${src?.parts}) + lots move to ${tgt?.stock_number}, and ${src?.stock_number} is archived (kept as history). ${tgt?.stock_number}'s acquisition cost is kept — costs are NOT summed.`)) return;
    setMerging(source);
    try {
      const { data } = await api.post(`/donors/${source}/merge-into`, { target_id: target });
      toast.success(`Merged ${data.source} → ${data.target} · ${data.parts_moved} parts, ${data.lots_moved} lots moved`);
      await load(); onMerged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setMerging(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-lg border-slate-200 sm:max-w-2xl" data-testid="donor-dup-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><GitMerge className="w-5 h-5 text-red-600" />Duplicate VIN trucks</DialogTitle>
        </DialogHeader>
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Pick the record to <b>KEEP</b> (usually the one with the most parts), then merge the others into it — one at a time. Parts + cost recovery consolidate onto the kept truck; the merged record is archived, never deleted. Verify the kept truck's cost afterward (both costs are in the audit).</span>
        </div>
        <div className="max-h-[55dvh] overflow-y-auto space-y-4 pr-1 mt-2">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
          ) : groups.length === 0 ? (
            <div data-testid="donor-dup-empty" className="text-center py-10 text-slate-400 text-sm"><CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-emerald-500" />No duplicate VINs. Every truck is unique. 🎉</div>
          ) : groups.map((g) => (
            <div key={g.vin} data-testid={`donor-dup-group-${g.vin}`} className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="rounded-full border-0 bg-slate-800 text-white font-mono text-[11px]">VIN {g.vin}</Badge>
                <Badge className="rounded-full border-0 bg-red-100 text-red-700 text-[11px]">{g.count} records</Badge>
              </div>
              <div className="space-y-1.5">
                {g.donors.map((d) => {
                  const keep = keepBy[g.vin] === d.id;
                  return (
                    <div key={d.id} data-testid={`donor-dup-row-${d.stock_number}`} className={`flex flex-wrap items-center gap-2 rounded-lg border px-2.5 py-2 ${keep ? "border-emerald-400 bg-emerald-50" : "border-slate-200"}`}>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" name={`keep-${g.vin}`} data-testid={`donor-dup-keep-${d.stock_number}`} checked={keep} onChange={() => setKeepBy((s) => ({ ...s, [g.vin]: d.id }))} className="accent-emerald-600" />
                        <span className="text-[11px] font-bold uppercase text-emerald-700">{keep ? "Keep" : "Keep"}</span>
                      </label>
                      <Truck className="w-4 h-4 text-[#B01E28] shrink-0" />
                      <button type="button" onClick={() => onOpenDonor?.(d.id)} className="font-mono text-sm font-bold hover:text-[#B01E28] inline-flex items-center gap-1">{d.stock_number}<ExternalLink className="w-3 h-3" /></button>
                      <span className="text-xs text-slate-500">{d.ymm}</span>
                      <Badge className="rounded-full border-0 bg-slate-100 text-slate-600 text-[10px] font-mono">{d.parts} parts</Badge>
                      <span className="text-[10px] text-slate-400 font-mono">{(d.created_at || "").slice(0, 10)}</span>
                      {d.is_primary && <Badge className="rounded-full border-0 bg-blue-100 text-blue-700 text-[10px]">primary</Badge>}
                      {d.quarantined && <Badge className="rounded-full border-0 bg-amber-100 text-amber-700 text-[10px]">quarantined</Badge>}
                      {!keep && (
                        <Button data-testid={`donor-dup-merge-${d.stock_number}`} size="sm" disabled={!!merging}
                          onClick={() => merge(g.vin, d.id, keepBy[g.vin])}
                          className="rounded-lg h-7 text-[11px] bg-red-600 hover:bg-red-700 text-white ml-auto">
                          {merging === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><GitMerge className="w-3.5 h-3.5 mr-1" />Merge into kept</>}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
